/**
 * useOrderInvitations Hook
 *
 * Manages all invitation-related state and operations for e-learning orders.
 * Handles data fetching, CRUD operations, and derived state calculations.
 *
 * Uses Redux operation status pattern for loading/error states (consistent with
 * Profile and User patterns in this codebase).
 *
 * @example
 * const {
 *   order,
 *   invitations,
 *   seatStats,
 *   isLoading,
 *   isSubmitting,
 *   error,
 *   createInvite,
 *   updateInvite,
 *   deleteInvite,
 * } = useOrderInvitations(orderId);
 */

import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_ERROR,
} from '@uncinc/react-drupal-core';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  fetchOrderDetail,
  fetchOrderInvitations,
  createInvitation,
  updateInvitation,
  deleteInvitation,
  InvitationError,
} from '../../../../../store/actions/Orders';

import type {
  OrderDetailResponse,
  ElearningInvitationsResponse,
  ElearningInvitation,
  InvitationErrorCode,
} from '../../../../../types/orders';

// =============================================================================
// Types
// =============================================================================

export type ErrorType = 'ORDER_NOT_FOUND' | 'UNAUTHORIZED' | 'FETCH_ERROR' | null;

export interface SeatStats {
  purchased: number;
  used: number;
  canInvite: boolean;
}

/** State for invitation-specific API errors with error codes */
export interface InvitationErrorState {
  errorCode: InvitationErrorCode | null;
  message: string | null;
}

export interface UseOrderInvitationsReturn {
  // Data
  order: OrderDetailResponse | null;
  invitations: ElearningInvitation[];
  invitationsData: ElearningInvitationsResponse | null;
  seatStats: SeatStats;

  // Loading/error state (from Redux)
  isLoading: boolean;
  error: ErrorType;
  isSubmitting: boolean;

  // Invitation operation error (NEW)
  invitationError: InvitationErrorState;
  clearInvitationError: () => void;

  // Actions
  createInvite: (email: string) => Promise<boolean>;
  updateInvite: (invitationId: number, email: string) => Promise<boolean>;
  deleteInvite: (invitationId: number) => Promise<void>;
}

// =============================================================================
// Helper: Get operation status from Redux state
// =============================================================================

const getOperationStatus = (
  state: ImmutableRootState<string>,
  operation: string,
): string => state.getIn(['ui', 'orders', 'operations', operation, 'status'], '');

// =============================================================================
// Hook Implementation
// =============================================================================

const useOrderInvitations = (orderId: string): UseOrderInvitationsReturn => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  // ---------------------------------------------------------------------------
  // Local State (data only - loading/error comes from Redux)
  // ---------------------------------------------------------------------------

  const [error, setError] = useState<ErrorType>(null);
  const [order, setOrder] = useState<OrderDetailResponse | null>(null);
  const [invitationsData, setInvitationsData] = useState<ElearningInvitationsResponse | null>(
    null,
  );

  // Invitation operation error state (for displaying user-friendly error messages)
  const [invitationError, setInvitationError] = useState<InvitationErrorState>({
    errorCode: null,
    message: null,
  });

  /** Clear invitation error (e.g., when user starts typing) */
  const clearInvitationError = useCallback(() => {
    setInvitationError({ errorCode: null, message: null });
  }, []);

  // ---------------------------------------------------------------------------
  // Redux Selectors for Operation Status
  // ---------------------------------------------------------------------------

  // Fetch operations
  const fetchDetailStatus = useSelector<ImmutableRootState<string>, string>(
    (state) => getOperationStatus(state, 'fetch_detail'),
  );
  const fetchInvitationsStatus = useSelector<ImmutableRootState<string>, string>(
    (state) => getOperationStatus(state, 'fetch_invitations'),
  );

  // Mutation operations
  const createInvitationStatus = useSelector<ImmutableRootState<string>, string>(
    (state) => getOperationStatus(state, 'create_invitation'),
  );
  const updateInvitationStatus = useSelector<ImmutableRootState<string>, string>(
    (state) => getOperationStatus(state, 'update_invitation'),
  );
  const deleteInvitationStatus = useSelector<ImmutableRootState<string>, string>(
    (state) => getOperationStatus(state, 'delete_invitation'),
  );

  // ---------------------------------------------------------------------------
  // Derived Loading States
  // ---------------------------------------------------------------------------

  /** Initial data loading */
  const isLoading = fetchDetailStatus === UI_OPERATION_STATUS_REQUEST
    || fetchInvitationsStatus === UI_OPERATION_STATUS_REQUEST;

  /** Any mutation in progress */
  const isSubmitting = createInvitationStatus === UI_OPERATION_STATUS_REQUEST
    || updateInvitationStatus === UI_OPERATION_STATUS_REQUEST
    || deleteInvitationStatus === UI_OPERATION_STATUS_REQUEST;

  // Note: We no longer use hasMutationError for the error state.
  // Mutation errors are now handled via invitationError state with specific error codes.
  // The generic error state is only for data fetch failures.

  // ---------------------------------------------------------------------------
  // Memoized Values
  // ---------------------------------------------------------------------------

  /** Calculate seat statistics from order data */
  const seatStats = useMemo((): SeatStats => {
    if (!order) return { purchased: 0, used: 0, canInvite: false };

    const purchased = order.quantity;
    const used = order.invite_stats?.invited || 0;

    return {
      purchased,
      used,
      canInvite: purchased > used,
    };
  }, [order]);

  /** Get invitations array with fallback */
  const invitations = useMemo(
    () => invitationsData?.invitations || [],
    [invitationsData],
  );

  // ---------------------------------------------------------------------------
  // Data Fetching
  // ---------------------------------------------------------------------------

  /** Fetch order detail and invitations in parallel */
  const fetchData = useCallback(async () => {
    setError(null);

    try {
      const [orderData, invitationsResponse] = await Promise.all([
        dispatch(fetchOrderDetail(orderId)),
        dispatch(fetchOrderInvitations(orderId)),
      ]);

      setOrder(orderData);
      setInvitationsData(invitationsResponse);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'FETCH_ERROR';

      const errorTypeMap: Record<string, ErrorType> = {
        ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
        UNAUTHENTICATED: 'UNAUTHORIZED',
      };

      setError(errorTypeMap[errorMessage] || 'FETCH_ERROR');
    }
  }, [dispatch, orderId]);

  /** Refresh invitations data after mutations */
  const refreshInvitations = useCallback(async () => {
    const newInvitations = await dispatch(fetchOrderInvitations(orderId));
    setInvitationsData(newInvitations);
  }, [dispatch, orderId]);

  /** Refresh both order and invitations data */
  const refreshAll = useCallback(async () => {
    const [newInvitations, newOrder] = await Promise.all([
      dispatch(fetchOrderInvitations(orderId)),
      dispatch(fetchOrderDetail(orderId)),
    ]);
    setInvitationsData(newInvitations);
    setOrder(newOrder);
  }, [dispatch, orderId]);

  // Fetch data on mount
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // Mutation Handlers
  // ---------------------------------------------------------------------------

  /** Create a new invitation - returns true on success, false on failure */
  const createInvite = useCallback(
    async (email: string): Promise<boolean> => {
      if (!order || !invitationsData) return false;

      // Clear any previous error
      clearInvitationError();

      try {
        await dispatch(
          createInvitation(
            order.order_id,
            // @ts-ignore - order.items[0] exists for e-learning orders
            order.items[0].order_item_id,
            email,
          ),
        );

        await refreshAll();
        return true;
      } catch (err) {
        // Extract error code from InvitationError
        if (err instanceof InvitationError) {
          setInvitationError({
            errorCode: err.errorCode,
            message: err.message,
          });
        } else {
          setInvitationError({
            errorCode: 'UNKNOWN_ERROR',
            message: err instanceof Error ? err.message : 'An error occurred',
          });
        }

        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Error creating invitation:', err);
        }
        return false;
      }
    },
    [dispatch, order, invitationsData, refreshAll, clearInvitationError],
  );

  /** Update an existing invitation's email - returns true on success */
  const updateInvite = useCallback(
    async (invitationId: number, email: string): Promise<boolean> => {
      // Clear any previous error
      clearInvitationError();

      try {
        await dispatch(updateInvitation(invitationId, email));
        await refreshInvitations();
        return true;
      } catch (err) {
        // Extract error code from InvitationError
        if (err instanceof InvitationError) {
          setInvitationError({
            errorCode: err.errorCode,
            message: err.message,
          });
        } else {
          setInvitationError({
            errorCode: 'UNKNOWN_ERROR',
            message: err instanceof Error ? err.message : 'An error occurred',
          });
        }

        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Error updating invitation:', err);
        }
        return false;
      }
    },
    [dispatch, refreshInvitations, clearInvitationError],
  );

  /** Delete an invitation */
  const deleteInvite = useCallback(
    async (invitationId: number): Promise<void> => {
      await dispatch(deleteInvitation(invitationId));
      await refreshAll();
    },
    [dispatch, refreshAll],
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // Data
    order,
    invitations,
    invitationsData,
    seatStats,

    // Loading/error state (derived from Redux)
    isLoading,
    error,
    isSubmitting,

    // Invitation operation error (for displaying user-friendly messages)
    invitationError,
    clearInvitationError,

    // Actions
    createInvite,
    updateInvite,
    deleteInvite,
  };
};

export default useOrderInvitations;
